import React, { useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import rehypeRaw from 'rehype-raw'
import Section from '../UI/Section'

const ToS: React.FC = () => {
  const [tosContent, setTosContent] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    const loadToSContent = async () => {
      try {
        const response = await fetch('/tos.md')
        if (!response.ok) {
          throw new Error(`Failed to load ToS: ${response.status}`)
        }
        const content = await response.text()
        setTosContent(content)
      } catch (err) {
        console.error('Error loading ToS:', err)
        setError('Failed to load Terms of Service')
      } finally {
        setIsLoading(false)
      }
    }

    loadToSContent()
  }, [])

  if (isLoading) {
    return (
      <Section sectionId="tos" backgroundImage="city_ruins.gif">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-900/90 backdrop-blur-sm rounded-lg p-4 sm:p-6 lg:p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
              <p className="mt-4 text-gray-300">Loading Terms of Service...</p>
            </div>
          </div>
        </div>
      </Section>
    )
  }

  if (error) {
    return (
      <Section sectionId="tos" backgroundImage="city_ruins.gif">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-900/90 backdrop-blur-sm rounded-lg p-4 sm:p-6 lg:p-8">
            <div className="text-center">
              <div className="text-red-500 text-xl mb-4">⚠️</div>
              <p className="text-red-400 text-lg">{error}</p>
            </div>
          </div>
        </div>
      </Section>
    )
  }

  return (
    <Section sectionId="tos" backgroundImage="city_ruins.gif">
      <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gray-900/90 backdrop-blur-sm rounded-lg p-4 sm:p-6 lg:p-8">
          <div className="prose prose-invert prose-red max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              rehypePlugins={[rehypeRaw]}
              components={{
                // Custom styling for markdown elements
                h1: ({ children }) => (
                  <h1 className="text-3xl sm:text-4xl font-bold text-white mb-6 border-b border-red-500 pb-4">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-2xl sm:text-3xl font-bold text-red-400 mt-8 mb-4">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-xl sm:text-2xl font-bold text-red-300 mt-6 mb-3">
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p className="text-gray-300 mb-4 leading-relaxed text-sm sm:text-base">
                    {children}
                  </p>
                ),
                ul: ({ children }) => (
                  <ul className="text-gray-300 mb-4 space-y-2 list-disc list-inside">
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="text-gray-300 mb-4 space-y-2 list-decimal list-inside">
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li className="text-gray-300 text-sm sm:text-base">
                    {children}
                  </li>
                ),
                strong: ({ children }) => (
                  <strong className="text-red-400 font-bold">
                    {children}
                  </strong>
                ),
                em: ({ children }) => (
                  <em className="text-red-300 italic">
                    {children}
                  </em>
                ),
                code: ({ children }) => (
                  <code className="bg-gray-800 text-red-300 px-2 py-1 rounded text-sm">
                    {children}
                  </code>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-red-500 pl-4 my-4 text-gray-400 italic">
                    {children}
                  </blockquote>
                ),
                a: ({ href, children }) => (
                  <a 
                    href={href} 
                    className="text-red-400 hover:text-red-300 underline transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {children}
                  </a>
                ),
                hr: () => (
                  <hr className="border-gray-600 my-8" />
                ),
              }}
            >
              {tosContent}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </Section>
  )
}

export default ToS

